import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../lib/getApiUrl';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  FileText, 
  Search, 
  Filter,
  RefreshCw,
  Eye,
  Edit,
  Save,
  X,
  Plus,
  Trash2
} from 'lucide-react';

interface ProjectQuestionnaire {
  id: string;
  projectId: string;
  question1?: string;
  question2?: string;
  question3?: string;
  question4?: string;
  question5?: string;
  question6?: string;
  question7?: string;
  answer1?: string;
  answer2?: string;
  answer3?: string;
  answer4?: string;
  answer5?: string;
  answer6?: string;
  answer7?: string;
  createdAt: string;
  updatedAt: string;
  project: {
    id: string;
    address: string;
    projectType?: string;
    priority: string;
    status: string;
    homeowner: {
      firstName: string;
      lastName: string;
      user: {
        email: string;
      };
    };
  };
}

const QuestionnaireManagement: React.FC = () => {
  const [questionnaires, setQuestionnaires] = useState<ProjectQuestionnaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [editingQuestionnaire, setEditingQuestionnaire] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<ProjectQuestionnaire>>({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({
    projectId: '',
    question1: '',
    question2: '',
    question3: '',
    question4: '',
    question5: '',
    question6: '',
    question7: ''
  });

  useEffect(() => {
    fetchQuestionnaires();
  }, []);

  const fetchQuestionnaires = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(getApiUrl('/api/admin/questionnaires'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('clerk_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch questionnaires');
      }

      const data = await response.json();
      setQuestionnaires(data.questionnaires || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchQuestionnaires();
    setRefreshing(false);
  };

  const updateQuestionnaire = async (questionnaireId: string, updates: Partial<ProjectQuestionnaire>) => {
    try {
      const response = await fetch(`/api/admin/questionnaires/${questionnaireId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('clerk_token')}`,
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error('Failed to update questionnaire');
      }

      await fetchQuestionnaires();
      setEditingQuestionnaire(null);
      setEditForm({});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update questionnaire');
    }
  };

  const createQuestionnaire = async () => {
    try {
      const response = await fetch(getApiUrl('/api/admin/questionnaires'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('clerk_token')}`,
        },
        body: JSON.stringify(createForm),
      });

      if (!response.ok) {
        throw new Error('Failed to create questionnaire');
      }

      await fetchQuestionnaires();
      setShowCreateForm(false);
      setCreateForm({
        projectId: '',
        question1: '',
        question2: '',
        question3: '',
        question4: '',
        question5: '',
        question6: '',
        question7: ''
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create questionnaire');
    }
  };

  const deleteQuestionnaire = async (questionnaireId: string) => {
    if (!confirm('Are you sure you want to delete this questionnaire?')) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/questionnaires/${questionnaireId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('clerk_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete questionnaire');
      }

      await fetchQuestionnaires();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete questionnaire');
    }
  };

  const filteredQuestionnaires = questionnaires.filter(questionnaire => {
    const matchesSearch = searchTerm === '' || 
      questionnaire.project.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      questionnaire.project.homeowner.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      questionnaire.project.homeowner.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      questionnaire.project.homeowner.user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'ALL' || questionnaire.project.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'COMPLETED':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'ON_HOLD':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'HIGH':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'LOW':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Loading questionnaire data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Questionnaire Management</h1>
          <p className="text-gray-600 mt-1">Manage project questionnaires and responses</p>
        </div>
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => setShowCreateForm(true)}
            variant="outline"
            size="sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Questionnaire
          </Button>
          <Button
            onClick={refreshData}
            disabled={refreshing}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertDescription className="text-red-800">
            {error}
          </AlertDescription>
        </Alert>
      )}

      {/* Create Questionnaire Form */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>Create New Questionnaire</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project ID
              </label>
              <input
                type="text"
                value={createForm.projectId}
                onChange={(e) => setCreateForm({ ...createForm, projectId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter project ID"
              />
            </div>
            
            {[1, 2, 3, 4, 5, 6, 7].map(num => (
              <div key={num} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Question {num}
                  </label>
                  <input
                    type="text"
                    value={createForm[`question${num}` as keyof typeof createForm]}
                    onChange={(e) => setCreateForm({ ...createForm, [`question${num}`]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={`Enter question ${num}`}
                  />
                </div>
              </div>
            ))}

            <div className="flex space-x-2">
              <Button onClick={createQuestionnaire} className="flex-1">
                <Save className="h-4 w-4 mr-2" />
                Create Questionnaire
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowCreateForm(false)}
                className="flex-1"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search questionnaires..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">All Statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="ON_HOLD">On Hold</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Questionnaires List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredQuestionnaires.map((questionnaire) => (
          <Card key={questionnaire.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between text-sm font-medium">
                <div className="flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-blue-500" />
                  <span className="text-gray-700 truncate">
                    {questionnaire.project.projectType || 'Project'}
                  </span>
                </div>
                <div className="flex space-x-1">
                  <Badge className={getStatusColor(questionnaire.project.status)}>
                    {questionnaire.project.status}
                  </Badge>
                  <Badge className={getPriorityColor(questionnaire.project.priority)}>
                    {questionnaire.project.priority}
                  </Badge>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Project Info */}
              <div>
                <p className="text-sm text-gray-600">{questionnaire.project.address}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {questionnaire.project.homeowner.firstName} {questionnaire.project.homeowner.lastName}
                </p>
                <p className="text-xs text-gray-500">{questionnaire.project.homeowner.user.email}</p>
              </div>

              {/* Questions Preview */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-900">Questions:</h4>
                {[1, 2, 3, 4, 5, 6, 7].map(num => {
                  const question = questionnaire[`question${num}` as keyof ProjectQuestionnaire] as string;
                  const answer = questionnaire[`answer${num}` as keyof ProjectQuestionnaire] as string;
                  
                  if (!question) return null;
                  
                  return (
                    <div key={num} className="text-xs">
                      <p className="font-medium text-gray-700">Q{num}: {question}</p>
                      {answer && (
                        <p className="text-gray-600 ml-2">A: {answer}</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Dates */}
              <div className="text-xs text-gray-500 space-y-1">
                <div>
                  <span className="font-medium">Created:</span> {new Date(questionnaire.createdAt).toLocaleDateString()}
                </div>
                <div>
                  <span className="font-medium">Updated:</span> {new Date(questionnaire.updatedAt).toLocaleDateString()}
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-2 pt-2 border-t">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditingQuestionnaire(questionnaire.id);
                    setEditForm(questionnaire);
                  }}
                  className="flex-1"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => deleteQuestionnaire(questionnaire.id)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              {/* Edit Form */}
              {editingQuestionnaire === questionnaire.id && (
                <div className="pt-2 border-t space-y-2">
                  {[1, 2, 3, 4, 5, 6, 7].map(num => (
                    <div key={num}>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Question {num}
                      </label>
                      <input
                        type="text"
                        value={editForm[`question${num}` as keyof ProjectQuestionnaire] as string || ''}
                        onChange={(e) => setEditForm({ ...editForm, [`question${num}`]: e.target.value })}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder={`Question ${num}`}
                      />
                    </div>
                  ))}
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      onClick={() => updateQuestionnaire(questionnaire.id, editForm)}
                      className="flex-1"
                    >
                      <Save className="h-4 w-4 mr-1" />
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingQuestionnaire(null);
                        setEditForm({});
                      }}
                      className="flex-1"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {filteredQuestionnaires.length === 0 && !loading && (
        <Card>
          <CardContent className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Questionnaires Found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || filterStatus !== 'ALL' 
                ? 'No questionnaires match your current filters.'
                : 'No questionnaires available. Questionnaires will appear here once projects are created.'
              }
            </p>
            {(searchTerm || filterStatus !== 'ALL') && (
              <Button 
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('ALL');
                }}
              >
                Clear Filters
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default QuestionnaireManagement;

